import { create } from "zustand";
import { supabase } from "@/integrations/supabase/client";
import { useAuthStore } from "./authStore";
import { useStoreManagementStore } from "./storeManagementStore";
import { Database } from "@/integrations/supabase/types";

type Json = Database["public"]["Tables"]["notifications"]["Row"]["metadata"];

export interface SystemNotification {
  id: string;
  store_id: string;
  user_id: string | null;
  type: string;
  title: string;
  message: string;
  entity_type: string | null;
  entity_id: string | null;
  metadata: Json;
  status: string;
  created_at: string;
  updated_at: string;
  is_read?: boolean;
}

interface NotificationState {
  notifications: SystemNotification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchNotifications: (storeId?: string) => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: (storeId?: string) => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  setupRealtimeSubscription: (storeId: string) => () => void;
  getUnreadCount: (storeId?: string) => Promise<number>;
  reset: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,

  fetchNotifications: async (storeId?: string) => {
    const currentStoreId = storeId || useStoreManagementStore.getState().selectedStoreId;
    const currentUser = useAuthStore.getState().user;

    if (!currentStoreId || !currentUser) {
      set({ notifications: [], isLoading: false });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("store_id", currentStoreId)
        .or(`user_id.eq.${currentUser.id},user_id.is.null`)
        .order("created_at", { ascending: false })
        .limit(100);

      if (error) throw error;

      set({ 
        notifications: data || [], 
        isLoading: false 
      });

      // Update unread count
      await get().getUnreadCount(currentStoreId);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      set({ 
        error: "Failed to fetch notifications", 
        isLoading: false 
      });
    }
  },

  markAsRead: async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from("notifications")
        .update({ status: "read" })
        .eq("id", notificationId);

      if (error) throw error;

      // Update local state
      set((state) => ({
        notifications: state.notifications.map((notification) =>
          notification.id === notificationId
            ? { ...notification, status: "read" }
            : notification
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  },

  markAllAsRead: async (storeId?: string) => {
    const currentStoreId = storeId || useStoreManagementStore.getState().selectedStoreId;
    const currentUser = useAuthStore.getState().user;

    if (!currentStoreId || !currentUser) return;

    try {
      const { error } = await supabase
        .from("notifications")
        .update({ status: "read" })
        .eq("store_id", currentStoreId)
        .or(`user_id.eq.${currentUser.id},user_id.is.null`)
        .eq("status", "unread");

      if (error) throw error;

      // Update local state
      set((state) => ({
        notifications: state.notifications.map((notification) => ({
          ...notification,
          status: "read",
        })),
        unreadCount: 0,
      }));
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  },

  deleteNotification: async (notificationId: string) => {
    try {
      
      const { data, error } = await supabase
        .from("notifications")
        .delete()
        .eq("id", notificationId)
        .select();

      if (error) {
        console.error("Database error deleting notification:", error);
        throw error;
      }


      // Update local state
      set((state) => {
        const deletedNotification = state.notifications.find(
          (n) => n.id === notificationId
        );
        const updatedNotifications = state.notifications.filter(
          (notification) => notification.id !== notificationId
        );
        
        
        return {
          notifications: updatedNotifications,
          unreadCount: deletedNotification?.status === "unread" 
            ? Math.max(0, state.unreadCount - 1) 
            : state.unreadCount,
        };
      });
    } catch (error) {
      console.error("Error deleting notification:", error);
      // Re-throw the error so the UI can handle it
      throw error;
    }
  },

  setupRealtimeSubscription: (storeId: string) => {
    const channel = supabase
      .channel("notifications-updates")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `store_id=eq.${storeId}`,
        },
        async (payload) => {
          if (payload.eventType === "INSERT") {
            // Add new notification
            const newNotification = payload.new as SystemNotification;
            set((state) => ({
              notifications: [newNotification, ...state.notifications],
              unreadCount: state.unreadCount + 1,
            }));
          } else if (payload.eventType === "UPDATE") {
            // Update existing notification
            const updatedNotification = payload.new as SystemNotification;
            set((state) => ({
              notifications: state.notifications.map((notification) =>
                notification.id === updatedNotification.id
                  ? updatedNotification
                  : notification
              ),
            }));
          } else if (payload.eventType === "DELETE") {
            // Remove notification
            const deletedNotification = payload.old as SystemNotification;
            set((state) => ({
              notifications: state.notifications.filter(
                (notification) => notification.id !== deletedNotification.id
              ),
              unreadCount: deletedNotification.status === "unread" 
                ? Math.max(0, state.unreadCount - 1) 
                : state.unreadCount,
            }));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  getUnreadCount: async (storeId?: string) => {
    const currentStoreId = storeId || useStoreManagementStore.getState().selectedStoreId;
    const currentUser = useAuthStore.getState().user;

    if (!currentStoreId || !currentUser) {
      set({ unreadCount: 0 });
      return 0;
    }

    try {
      const { count, error } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("store_id", currentStoreId)
        .or(`user_id.eq.${currentUser.id},user_id.is.null`)
        .eq("status", "unread");

      if (error) throw error;

      const unreadCount = count || 0;
      set({ unreadCount });
      return unreadCount;
    } catch (error) {
      console.error("Error getting unread count:", error);
      set({ unreadCount: 0 });
      return 0;
    }
  },

  reset: () => {
    set({
      notifications: [],
      unreadCount: 0,
      isLoading: false,
      error: null,
    });
  },
})); 